import { SagaIterator } from 'redux-saga';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { Messages } from '../../../codegen/Localize';
import { IExplorableItemTypeInfo } from '../../../ts/models/resourceInfoParts';
import { IResult } from '../../../ts/ServerApi/types/IResponse';
import { ContentActions } from '../../../ts/services/ContentActions';
import { CreateScheduleAction } from '../../../ts/services/CreateScheduleAction';
import { CreateSiteAction } from '../../../ts/services/CreateSiteAction';
import { CreateNewContentInfoRequest, CreateNewFlow, CreateNewProject, CreateNewSchedule, CreateNewSite, CreateNewWorkbook } from '../../actions/AppActionTypes';
import { requestFailed } from '../../actions/RequestFailedAction';
import {
  requestAvailableActionsForEntity
} from '../../actions/spaces/AvailableActionsForEntityActions';
import { CreateNewContentInfoRequestAction, CreateNewProjectAction, CreateNewScheduleAction, CreateNewSiteAction, CreateNewWorkbookAction } from '../../actions/spaces/ContentExplorerCreateActions';
import {
  requestSpaceContents
} from '../../actions/spaces/RequestSpaceContentsActions';
import { selectSpaceInfo } from '../../selectors/ContentSelectors';
import {
  SessionSiteSelectors
} from '../../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { SpaceInfo } from '../../state/SpaceInfo';

export function* handleCreateNewContentInfoRequest(action: CreateNewContentInfoRequestAction) {
  // if app state doesnt have all the data it needs, make those API calls here.
  try {
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    yield put(requestAvailableActionsForEntity(spaceInfo.item as IExplorableItemTypeInfo));
  } catch(e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Workbook

const createNewWorbook = (siteUrlName: string, projectId: string) => {
  const contentActionService = ServiceLocator.get<ContentActions>('ContentActions');
  contentActionService.newEmptyWorkbook(siteUrlName, projectId);
};

export function* handleCreateNewWorkbook(action: CreateNewWorkbookAction) {
  try {
    const siteUrlName: string = yield select(SessionSiteSelectors.siteUrlName);
    yield call(createNewWorbook, siteUrlName, action.projectId || '');
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Project

const createNewProject = (projectId: string): ng.IPromise<IResult> => {
  const contentActionService = ServiceLocator.get<ContentActions>('ContentActions');
  const context = ServiceLocator.getAngularActionContext();
  return contentActionService.createProject(context, projectId);
};

export function* handleCreateNewProject(action: CreateNewProjectAction) {
  try {
    yield call(createNewProject, action.projectId);

    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Flow

export function* handleCreateNewFlow() {
  try {
    const siteUrlName: string = yield select(SessionSiteSelectors.siteUrlName);
    const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
    ServiceLocator.get<ContentActions>('ContentActions').newFlow(siteUrlName, spaceInfo.item);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create New Site

const createNewSite = (): ng.IPromise<IResult> => {
  const createSiteActionService = ServiceLocator.get<CreateSiteAction>('CreateSiteAction');
  const context = ServiceLocator.getAngularActionContext();
  return createSiteActionService.execute(context.scope);
};

export function* handleCreateNewSite(action: CreateNewSiteAction) {
  try {
    yield call(createNewSite);
    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

// Create new schedule

const createNewSchedule = (): ng.IPromise<IResult> => {
  const createScheduleActionService = ServiceLocator.get<CreateScheduleAction>('CreateScheduleAction');
  const context = ServiceLocator.getAngularActionContext();
  return createScheduleActionService.createSchedule(context);
};

export function* handleCreateNewSchedule(action: CreateNewScheduleAction) {
  try {
    yield call(createNewSchedule);
    yield put(requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootContentCreationSaga(): SagaIterator {
  yield takeLatest(CreateNewContentInfoRequest, handleCreateNewContentInfoRequest);
  yield takeLatest(CreateNewWorkbook, handleCreateNewWorkbook);
  yield takeLatest(CreateNewProject, handleCreateNewProject);
  yield takeLatest(CreateNewFlow, handleCreateNewFlow);
  yield takeLatest(CreateNewSite, handleCreateNewSite);
  yield takeLatest(CreateNewSchedule, handleCreateNewSchedule);
}
